import React, { useState, useEffect } from 'react';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';

const card: React.CSSProperties = {
  background: 'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))',
  border: '1px solid rgba(255,255,255,0.09)', borderRadius: '12px',
  padding: '1.25rem 1.4rem', marginBottom: '1.25rem',
};
const sectionLabel: React.CSSProperties = {
  fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: '0.85rem', fontWeight: 600,
};

interface AppSettings {
  paymentsEnabled: boolean;
  standardTournamentsEnabled: boolean;
  freeTournamentsCount: number;
  recaptchaEnabled: boolean;
  timedPricePence: number;
  standardPricePence: number;
  googleLoginEnabled: boolean;
  facebookLoginEnabled: boolean;
  leaguesEnabled: boolean;
}

export default function SuperuserSettingsPage() {
  const { getAccessToken } = useAuth();
  const { t } = useLang();
  const [settings, setSettings]   = useState<AppSettings | null>(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [saved, setSaved]         = useState(false);
  const [freeCount, setFreeCount] = useState(1);
  const [freeSaving, setFreeSaving] = useState(false);
  const [freeSaved, setFreeSaved] = useState(false);
  const [timedPence, setTimedPence] = useState(500);
  const [standardPence, setStandardPence] = useState(250);
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceSaved, setPriceSaved] = useState(false);

  const authed = async (path: string, method = 'GET', body?: object) => {
    const token = await getAccessToken();
    const res = await fetch(`/api/auth${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json() as Record<string, unknown> & { error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Failed');
    return data;
  };

  useEffect(() => {
    authed('/settings')
      .then(d => { const s = { paymentsEnabled: Boolean(d.paymentsEnabled), standardTournamentsEnabled: Boolean(d.standardTournamentsEnabled), freeTournamentsCount: Number(d.freeTournamentsCount ?? 1), recaptchaEnabled: d.recaptchaEnabled !== false, timedPricePence: Number(d.timedPricePence ?? 500), standardPricePence: Number(d.standardPricePence ?? 250), googleLoginEnabled: d.googleLoginEnabled !== false, facebookLoginEnabled: d.facebookLoginEnabled !== false, leaguesEnabled: Boolean(d.leaguesEnabled) }; setSettings(s); setFreeCount(s.freeTournamentsCount); setTimedPence(s.timedPricePence); setStandardPence(s.standardPricePence); })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleSaveFreeTournaments = async () => {
    if (!settings) return;
    const val = Math.max(0, Math.floor(freeCount));
    setFreeSaving(true); setFreeSaved(false); setError(null);
    try {
      const result = await authed('/settings', 'PUT', { freeTournamentsCount: val }) as { freeTournamentsCount?: number };
      const saved_val = typeof result.freeTournamentsCount === 'number' ? result.freeTournamentsCount : val;
      setSettings({ ...settings, freeTournamentsCount: saved_val });
      setFreeCount(saved_val);
      setFreeSaved(true);
      setTimeout(() => setFreeSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setFreeSaving(false); }
  };

  const handleToggleRecaptcha = async () => {
    if (!settings) return;
    const newVal = !settings.recaptchaEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { recaptchaEnabled: newVal });
      setSettings({ ...settings, recaptchaEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSavePrices = async () => {
    if (!settings) return;
    const timed    = Math.max(0, Math.floor(timedPence));
    const standard = Math.max(0, Math.floor(standardPence));
    setPriceSaving(true); setPriceSaved(false); setError(null);
    try {
      const result = await authed('/settings', 'PUT', { timedPricePence: timed, standardPricePence: standard }) as { timedPricePence?: number; standardPricePence?: number };
      setSettings({ ...settings, timedPricePence: result.timedPricePence ?? timed, standardPricePence: result.standardPricePence ?? standard });
      setTimedPence(result.timedPricePence ?? timed);
      setStandardPence(result.standardPricePence ?? standard);
      setPriceSaved(true);
      setTimeout(() => setPriceSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setPriceSaving(false); }
  };

  const handleToggleStandard = async () => {
    if (!settings) return;
    const newVal = !settings.standardTournamentsEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { standardTournamentsEnabled: newVal });
      setSettings({ ...settings, standardTournamentsEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggleLeagues = async () => {
    if (!settings) return;
    const newVal = !settings.leaguesEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { leaguesEnabled: newVal });
      setSettings({ ...settings, leaguesEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggleGoogleLogin = async () => {
    if (!settings) return;
    const newVal = !settings.googleLoginEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { googleLoginEnabled: newVal });
      setSettings({ ...settings, googleLoginEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggleFacebookLogin = async () => {
    if (!settings) return;
    const newVal = !settings.facebookLoginEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { facebookLoginEnabled: newVal });
      setSettings({ ...settings, facebookLoginEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTogglePayments = async () => {
    if (!settings) return;
    const newVal = !settings.paymentsEnabled;
    setSaving(true); setSaved(false); setError(null);
    try {
      await authed('/settings', 'PUT', { paymentsEnabled: newVal });
      setSettings({ ...settings, paymentsEnabled: newVal });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.4rem)', color: 'var(--gold)', marginBottom: '0.3rem' }}>
        ⚙ Site Settings
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.75rem' }}>
        Superuser-only configuration for the entire application.
      </p>

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '0.65rem 1rem', color: '#e74c3c', fontSize: '0.82rem', marginBottom: '1rem' }}>
          ⚠ {error}
        </div>
      )}

      {loading && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem' }}>{t.loadingLabel}</p>}

      {settings && (
        <>
          {/* Payment system toggle */}
          <div style={card}>
            <h3 style={sectionLabel}>💳 Payment System</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              When enabled, users must pay the configured price to create each tournament after their free allowance. Prices are set in the Tournament Pricing section below.
              Superusers and users granted free tournaments for life are never charged.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleTogglePayments}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: settings.paymentsEnabled ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${settings.paymentsEnabled ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                  fontSize: '0.88rem', color: settings.paymentsEnabled ? '#2ecc71' : 'var(--chalk-dim)',
                  fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                }}
              >
                <span style={{
                  width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0,
                  background: settings.paymentsEnabled ? '#2ecc71' : 'rgba(255,255,255,0.15)',
                  display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s',
                }}>
                  <span style={{
                    width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                    transform: settings.paymentsEnabled ? 'translateX(14px)' : 'translateX(0)',
                    transition: 'transform 0.2s', display: 'block', flexShrink: 0,
                  }} />
                </span>
                {saving ? 'Saving…' : settings.paymentsEnabled ? 'Payments ENABLED' : 'Payments DISABLED'}
              </button>
              {saved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>

            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--chalk)' }}>{t.settingsCurrentState}</strong>{' '}
                {settings.paymentsEnabled
                  ? `🔴 Payments are ON — timed: £${((settings.timedPricePence ?? 500) / 100).toFixed(2)}, standard: £${((settings.standardPricePence ?? 250) / 100).toFixed(2)}.`
                  : '🟢 Payments are OFF — all users can create unlimited tournaments for free.'}
              </p>
            </div>
          </div>

          {/* Free tournaments count */}
          <div style={card}>
            <h3 style={sectionLabel}>🎟 Free Tournament Allowance</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              The number of free tournaments each user gets before being required to pay. Set to <strong style={{ color: 'var(--chalk)' }}>0</strong> to charge for every tournament, or a higher number to give users more free ones.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input
                type="number"
                min={0}
                max={100}
                value={freeCount}
                onChange={e => setFreeCount(Math.max(0, parseInt(e.target.value, 10) || 0))}
                style={{ width: '72px', padding: '0.45rem 0.6rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: 'var(--gold)', fontFamily: 'Bebas Neue', fontSize: '1.6rem', textAlign: 'center', outline: 'none' }}
              />
              <button
                onClick={handleSaveFreeTournaments}
                disabled={freeSaving}
                style={{ padding: '0.5rem 1.1rem', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', fontWeight: 600, fontSize: '0.85rem', cursor: freeSaving ? 'not-allowed' : 'pointer', opacity: freeSaving ? 0.6 : 1 }}
              >
                {freeSaving ? 'Saving…' : 'Save'}
              </button>
              {freeSaved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
              <span style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)' }}>
                {settings.freeTournamentsCount === 0
                  ? 'All tournaments require payment'
                  : settings.freeTournamentsCount === 1
                  ? 'First tournament is free, then payment required'
                  : `First ${settings.freeTournamentsCount} tournaments are free`}
              </span>
            </div>
          </div>

          {/* Standard tournaments toggle */}
          <div style={card}>
            <h3 style={sectionLabel}>🔴 Standard Snooker Tournaments</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              When enabled, users can choose between <strong style={{ color: 'var(--chalk)' }}>{t.settingsTimedLabel}</strong> (Shootout/Snooker 900) and <strong style={{ color: 'var(--chalk)' }}>{t.settingsStandardLabel}</strong> (no time limit) when creating a tournament. Prices are configured below.
              When disabled, the type picker is skipped and the timed dialog opens directly.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleToggleStandard}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: settings.standardTournamentsEnabled ? 'rgba(52,152,219,0.12)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${settings.standardTournamentsEnabled ? 'rgba(52,152,219,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                  fontSize: '0.88rem', color: settings.standardTournamentsEnabled ? '#3498db' : 'var(--chalk-dim)',
                  fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                }}
              >
                <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: settings.standardTournamentsEnabled ? '#3498db' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: settings.standardTournamentsEnabled ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
                </span>
                {saving ? 'Saving…' : settings.standardTournamentsEnabled ? 'Standard Tournaments ENABLED' : 'Standard Tournaments DISABLED'}
              </button>
              {saved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--chalk)' }}>{t.settingsCurrentState}</strong>{' '}
                {settings.standardTournamentsEnabled
                  ? '🟢 Standard tournaments ON — users will be shown a type picker when creating a tournament.'
                  : '🔴 Standard tournaments OFF — timed tournament dialog opens directly.'}
              </p>
            </div>
          </div>

          {/* Leagues toggle — platform-wide kill switch for the experimental Leagues feature */}
          <div style={card}>
            <h3 style={sectionLabel}>🏆 Leagues</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Experimental, in-development feature. When enabled, a "Leagues" destination appears in the nav for every user. Off by default so it stays invisible until it's ready.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleToggleLeagues}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: settings.leaguesEnabled ? 'rgba(155,89,182,0.12)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${settings.leaguesEnabled ? 'rgba(155,89,182,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                  fontSize: '0.88rem', color: settings.leaguesEnabled ? '#9b59b6' : 'var(--chalk-dim)',
                  fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                }}
              >
                <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: settings.leaguesEnabled ? '#9b59b6' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: settings.leaguesEnabled ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
                </span>
                {saving ? 'Saving…' : settings.leaguesEnabled ? 'Leagues ENABLED' : 'Leagues DISABLED'}
              </button>
              {saved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--chalk)' }}>{t.settingsCurrentState}</strong>{' '}
                {settings.leaguesEnabled
                  ? '🟢 Leagues ON — the nav shows a Leagues destination for every user.'
                  : '🔴 Leagues OFF — hidden from all users.'}
              </p>
            </div>
          </div>

          {/* Tournament pricing */}
          <div style={card}>
            <h3 style={sectionLabel}>💰 Tournament Pricing</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Set the price (in pence) users pay to create each tournament type after their free allowance.
              Enter <strong style={{ color: 'var(--chalk)' }}>0</strong> to make a format free.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1rem' }}>
              {/* Timed price */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', minWidth: '120px' }}>⚡ Timed (pence)</label>
                <input
                  type="number" min={0} max={99999} value={timedPence}
                  onChange={e => setTimedPence(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  style={{ width: '90px', padding: '0.4rem 0.6rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: 'var(--gold)', fontFamily: 'Bebas Neue', fontSize: '1.4rem', textAlign: 'center', outline: 'none' }}
                />
                <span style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)' }}>
                  = <strong style={{ color: 'var(--chalk)' }}>£{(timedPence / 100).toFixed(2)}</strong>
                </span>
              </div>
              {/* Standard price */}
              {settings.standardTournamentsEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', minWidth: '120px' }}>🔴 Standard (pence)</label>
                  <input
                    type="number" min={0} max={99999} value={standardPence}
                    onChange={e => setStandardPence(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    style={{ width: '90px', padding: '0.4rem 0.6rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: 'var(--gold)', fontFamily: 'Bebas Neue', fontSize: '1.4rem', textAlign: 'center', outline: 'none' }}
                  />
                  <span style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)' }}>
                    = <strong style={{ color: 'var(--chalk)' }}>£{(standardPence / 100).toFixed(2)}</strong>
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                onClick={handleSavePrices}
                disabled={priceSaving}
                style={{ padding: '0.5rem 1.1rem', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', fontWeight: 600, fontSize: '0.85rem', cursor: priceSaving ? 'not-allowed' : 'pointer', opacity: priceSaving ? 0.6 : 1 }}
              >
                {priceSaving ? 'Saving…' : t.settingsSavePrices}
              </button>
              {priceSaved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>
          </div>

          {/* reCAPTCHA toggle */}
          <div style={card}>
            <h3 style={sectionLabel}>🔒 reCAPTCHA (Contact Form)</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              When enabled, the contact form uses Google reCAPTCHA v3 to detect bots. Requires{' '}
              <code style={{ background: 'rgba(255,255,255,0.07)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.78rem' }}>RECAPTCHA_SITE_KEY</code>{' '}and{' '}
              <code style={{ background: 'rgba(255,255,255,0.07)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.78rem' }}>RECAPTCHA_SECRET_KEY</code>{' '}in the server <code style={{ background: 'rgba(255,255,255,0.07)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.78rem' }}>.env</code>.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleToggleRecaptcha}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: settings.recaptchaEnabled ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${settings.recaptchaEnabled ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                  fontSize: '0.88rem', color: settings.recaptchaEnabled ? '#2ecc71' : 'var(--chalk-dim)',
                  fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                }}
              >
                <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: settings.recaptchaEnabled ? '#2ecc71' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: settings.recaptchaEnabled ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
                </span>
                {saving ? 'Saving…' : settings.recaptchaEnabled ? 'reCAPTCHA ENABLED' : 'reCAPTCHA DISABLED'}
              </button>
              {saved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--chalk)' }}>{t.settingsCurrentState}</strong>{' '}
                {settings.recaptchaEnabled
                  ? '🟢 reCAPTCHA ON — bot protection active on the contact form (only applies if env keys are set).'
                  : '🔴 reCAPTCHA OFF — contact form accepts submissions without verification.'}
              </p>
            </div>
          </div>

          {/* Social login toggles */}
          <div style={card}>
            <h3 style={sectionLabel}>🔑 Social Login</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--chalk-dim)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Controls whether the "Continue with Google" / "Continue with Facebook" buttons appear on the sign-in and sign-up pages. Disabling a provider here also blocks it server-side — existing accounts linked to a disabled provider must sign in another way until it's re-enabled.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleToggleGoogleLogin}
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    background: settings.googleLoginEnabled ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${settings.googleLoginEnabled ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                    fontSize: '0.88rem', color: settings.googleLoginEnabled ? '#2ecc71' : 'var(--chalk-dim)',
                    fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                  }}
                >
                  <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: settings.googleLoginEnabled ? '#2ecc71' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                    <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: settings.googleLoginEnabled ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
                  </span>
                  {saving ? 'Saving…' : settings.googleLoginEnabled ? 'Google Login ENABLED' : 'Google Login DISABLED'}
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleToggleFacebookLogin}
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    background: settings.facebookLoginEnabled ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${settings.facebookLoginEnabled ? 'rgba(46,204,113,0.4)' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: '8px', padding: '0.55rem 0.9rem', cursor: 'pointer',
                    fontSize: '0.88rem', color: settings.facebookLoginEnabled ? '#2ecc71' : 'var(--chalk-dim)',
                    fontWeight: 600, minHeight: '44px', transition: 'all 0.2s',
                  }}
                >
                  <span style={{ width: '34px', height: '20px', borderRadius: '10px', flexShrink: 0, background: settings.facebookLoginEnabled ? '#2ecc71' : 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s' }}>
                    <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transform: settings.facebookLoginEnabled ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block', flexShrink: 0 }} />
                  </span>
                  {saving ? 'Saving…' : settings.facebookLoginEnabled ? 'Facebook Login ENABLED' : 'Facebook Login DISABLED'}
                </button>
              </div>
              {saved && <span style={{ color: '#2ecc71', fontSize: '0.8rem' }}>✓ Saved</span>}
            </div>
          </div>

          {/* Info card */}
          <div style={{ ...card, borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
            <p style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: 'var(--chalk)' }}>{t.settingsPerUserOverrides}</strong> are managed on the{' '}
              <strong style={{ color: 'var(--chalk)' }}>Users</strong> page — toggle the <strong style={{ color: '#f1c40f' }}>♾ Free</strong> button on any user's card to grant or revoke free tournaments for life regardless of the global setting.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
